import { HttpClient } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable } from '@angular/core';

import { BehaviorSubject, catchError, distinctUntilChanged, finalize, map, Observable, of, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AdvancedMarkerElementOptionsCustom } from '../modules/facility-information/google-map/models/advanced-marker-element-options';

@Injectable({
    providedIn: 'root',
})
export class GoogleMapService {
    private _isGoogleMapsLoaded = new BehaviorSubject<boolean>(false);
    private _isLoadingGoogleMaps = new BehaviorSubject<boolean>(false);
    private _advancedMarkerElement;

    constructor(
        private httpClient: HttpClient,
        @Inject(DOCUMENT) private document: Document
    ) {}

    setAPILoaded(isLoaded: boolean): void {
        this._isGoogleMapsLoaded.next(isLoaded);
    }

    isGoogleMapsLoaded(): Observable<boolean> {
        return this._isGoogleMapsLoaded.asObservable().pipe(distinctUntilChanged());
    }

    setIsLoading(isLoaded: boolean): void {
        this._isLoadingGoogleMaps.next(isLoaded);
    }

    isLoadingGoogleMaps(): Observable<boolean> {
        return this._isLoadingGoogleMaps.asObservable().pipe(distinctUntilChanged());
    }

    loadAPI(): Observable<boolean> {
        this.setIsLoading(true);

        return this.isGoogleMapsLoaded().pipe(
            switchMap((isGoogleMapsLoaded) => {
                if (isGoogleMapsLoaded) {
                    this.setIsLoading(false);
                    return of(true);
                }

                return this.httpClient
                    .jsonp(
                        `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapApiKey}&libraries=places&callback=initMap`,
                        'callback'
                    )
                    .pipe(
                        map(() => {
                            this.setAPILoaded(true);

                            return true;
                        }),
                        catchError(() => {
                            this.setAPILoaded(false);
                            return of(false);
                        }),
                        finalize(() => {
                            this.setIsLoading(false);
                        })
                    );
            }),
            catchError(() => {
                this.setAPILoaded(false);
                this.setIsLoading(false);
                return of(false);
            }),
            finalize(() => {
                this.setIsLoading(false);
            })
        );
    }

    /**
     * Creates a google map marker with the map context
     */
    createMarker(
        options: google.maps.marker.AdvancedMarkerElementOptions = {}
    ): google.maps.marker.AdvancedMarkerElement {
        return new this._advancedMarkerElement(options);
    }

    getCenterMap(markers: google.maps.marker.AdvancedMarkerElement[]): google.maps.LatLng {
        const bounds = new google.maps.LatLngBounds();

        for (let i = 0; i < markers.length; i++) {
            if (markers[i] && markers?.[i]?.position) {
                bounds.extend(markers[i].position!);
            }
        }

        return bounds.getCenter();
    }

    getMarkersPositions(markers: google.maps.marker.AdvancedMarkerElement[]): google.maps.LatLngLiteral[] {
        return markers.reduce((result: google.maps.LatLngLiteral[], marker) => {
            if (marker) {
                const position = marker.position;
                const lat = typeof position?.lat === 'function' ? (position.lat as () => number)() : position?.lat;
                const lng = typeof position?.lng === 'function' ? (position.lng as () => number)() : position?.lng;

                if (lat && lng) {
                    result.push({
                        lat,
                        lng,
                    });
                }
            }

            return result;
        }, [] as google.maps.LatLngLiteral[]);
    }

    async createMarkers(
        markers: AdvancedMarkerElementOptionsCustom[]
    ): Promise<google.maps.marker.AdvancedMarkerElement[]> {
        const { AdvancedMarkerElement, PinElement } = (await google.maps.importLibrary(
            'marker'
        )) as google.maps.MarkerLibrary;

        return markers.reduce((result, info) => {
            if (info?.position) {
                const glyphImg = this.document.createElement('img');
                glyphImg.src = info?.icon || '';

                const glyphSvgPinElement = new PinElement({
                    glyph: glyphImg,
                });

                const marker = new AdvancedMarkerElement({
                    position: new google.maps.LatLng(info.position),
                    content: glyphSvgPinElement.element,
                    gmpClickable: info.gmpClickable,
                    title: info.title,
                    gmpDraggable: info.gmpDraggable,
                });

                result.push(marker);
            }

            return result;
        }, [] as google.maps.marker.AdvancedMarkerElement[]);
    }
}
